import { graphql, useStaticQuery } from "gatsby"
import { useMemo } from "react"

type GeoSearchOption = {
  label: string
  value: string
  type: "city" | "country" | "region" | "landmark" | "state" | "destination"
  slug: string
  external_id: number
  countryName?: string
  countryId?: string
  disabled?: boolean
}

export const useGeoSearchData = () => {
  const data = useStaticQuery(graphql`
    query GeoSearchData {
      allCity {
        nodes {
          id
          title
          slug
          external_id
          country {
            title
            external_id
          }
        }
      }
      allCountry {
        nodes {
          id
          title
          slug
          external_id
        }
      }
      allRegion {
        nodes {
          id
          title
          slug
          external_id
          country {
            title
            external_id
          }
        }
      }
      allLandmark {
        nodes {
          id
          title
          slug
          external_id
          country {
            title
            external_id
          }
        }
      }
      allState {
        nodes {
          id
          title
          slug
          external_id
          country {
            title
            external_id
          }
        }
      }
      allDestination {
        nodes {
          id
          title
          slug
          external_id
        }
      }
    }
  `)

  const allGeoOptions: GeoSearchOption[] = useMemo(() => {
    const options: GeoSearchOption[] = []

    // Add cities with null safety
    const cities = data?.allCity?.nodes || []
    cities.forEach((city: any) => {
      if (city.title && city.external_id && city.slug) {
        options.push({
          label: city.title,
          value: city.external_id.toString(),
          type: "city",
          slug: city.slug,
          external_id: city.external_id,
          countryName: city.country?.title,
          countryId: city.country?.external_id?.toString(),
        })
      }
    })

    // Add countries with null safety
    const countries = data?.allCountry?.nodes || []
    countries.forEach((country: any) => {
      if (country.title && country.external_id && country.slug) {
        options.push({
          label: country.title,
          value: country.external_id.toString(),
          type: "country",
          slug: country.slug,
          external_id: country.external_id,
        })
      }
    })

    // Add regions with null safety
    const regions = data?.allRegion?.nodes || []
    regions.forEach((region: any) => {
      if (region.title && region.external_id && region.slug) {
        options.push({
          label: region.title,
          value: region.external_id.toString(),
          type: "region",
          slug: region.slug,
          external_id: region.external_id,
          countryName: region.country?.title,
          countryId: region.country?.external_id?.toString(),
        })
      }
    })

    // Add landmarks with null safety
    const landmarks = data?.allLandmark?.nodes || []
    landmarks.forEach((landmark: any) => {
      if (landmark.title && landmark.external_id && landmark.slug) {
        options.push({
          label: landmark.title,
          value: landmark.external_id.toString(),
          type: "landmark",
          slug: landmark.slug,
          external_id: landmark.external_id,
          countryName: landmark.country?.title,
          countryId: landmark.country?.external_id?.toString(),
        })
      }
    })

    // Add states with null safety
    const states = data?.allState?.nodes || []
    states.forEach((state: any) => {
      if (state.title && state.external_id && state.slug) {
        options.push({
          label: state.title,
          value: state.external_id.toString(),
          type: "state",
          slug: state.slug,
          external_id: state.external_id,
          countryName: state.country?.title,
          countryId: state.country?.external_id?.toString(),
        })
      }
    })

    // Add destinations with null safety
    const destinations = data?.allDestination?.nodes || []
    destinations.forEach((destination: any) => {
      if (destination.title && destination.external_id && destination.slug) {
        options.push({
          label: destination.title,
          value: destination.external_id.toString(),
          type: "destination",
          slug: destination.slug,
          external_id: destination.external_id,
        })
      }
    })

    // Sort with null safety
    return options.sort((a, b) => {
      if (!a.label || !b.label) return 0
      return a.label.localeCompare(b.label)
    })
  }, [data])

  const searchGeoOptions = (query: string, limit: number = 10): GeoSearchOption[] => {
    if (!query || query.length < 3 || !Array.isArray(allGeoOptions)) {
      return []
    }

    const lowercaseQuery = query.toLowerCase()
    return allGeoOptions
      .filter((option) => option?.label && option.label.toLowerCase().includes(lowercaseQuery))
      .slice(0, limit)
  }

  return {
    allGeoOptions,
    searchGeoOptions,
  }
} 